import { useState } from "react";
import {
  ArrowCounterClockwise,
  ArrowRight,
  Calculator,
  DotsSixVertical,
  Eye,
  EyeSlash,
  PencilSimple,
  Plus,
  PaperPlaneTilt,
  Trash,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SearchSelect } from "@/components/SearchSelect";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { money } from "@/lib/format";
import CostMarginDialog from "@/components/CostMarginDialog";
import { MaterialOptionRow } from "@/components/MaterialOptionRow";

const COST_KINDS = new Set(["material", "backsplash", "product", "manual"]);

/** Margin % a line earns against its saved cost; null when no cost is recorded. */
export const lineMarginPct = (line) => {
  const cost =
    line.cost_total != null && line.cost_total !== ""
      ? Number(line.cost_total)
      : line.unit_cost != null && line.unit_cost !== ""
        ? Number(line.unit_cost) * Number(line.qty || 0)
        : null;
  if (cost == null || !Number.isFinite(cost)) return null;
  const total = Number(line.total || 0);
  if (!total) return null;
  return Math.round(((total - cost) / total) * 1000) / 10;
};

const slug = (v) =>
  String(v || "")
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();

const keyOf = (l) => (l.manual ? `m:${l.id}` : `k:${l.key}`);

const LINE_TYPES = [
  { kind: "material", label: "Material" },
  { kind: "service", label: "Services" },
  { kind: "cabinet", label: "Cabinetry" },
  { kind: "sink", label: "Sink" },
  { kind: "slab", label: "Slab" },
  { kind: "remnant", label: "Remnant" },
  { kind: "custom", label: "Custom" },
];

const KIND_LABEL = {  material: "Material",
  edge: "Edge",
  cutout: "Cut-out",
  backsplash: "Splash",
  install: "Install",
  template: "Template",
  product: "Product",
  manual: "Manual",
};

export const resolveLine = (ln, edits) => {
  const ed =
    (edits || []).find((e) =>
      ln.manual ? e.id && e.id === ln.id : e.key && e.key === ln.key,
    ) || {};
  const qty = ed.qty ?? ln.qty;
  const unitPrice = ed.unit_price ?? ln.unit_price;
  return {
    ...ln,
    item: ed.item ?? ln.item,
    description: ed.description ?? ln.description,
    unit: ed.unit ?? ln.unit,
    qty,
    unit_price: unitPrice,
    taxable: ed.taxable ?? ln.taxable,
    hidden: ed.hidden ?? ln.hidden,
    is_material: ed.is_material ?? ln.is_material,
    unit_cost: ed.unit_cost ?? ln.unit_cost,
    cost_total: ed.cost_total ?? ln.cost_total,
    cost_mode: ed.cost_mode ?? ln.cost_mode,
    cost_pct: ed.cost_pct ?? ln.cost_pct,
    cost_basis: ed.cost_basis ?? ln.cost_basis,
    cost_supplier: ed.cost_supplier ?? ln.cost_supplier,
    cost_tax_pct: ed.cost_tax_pct ?? ln.cost_tax_pct,
    cost_other: ed.cost_other ?? ln.cost_other,
    cost_width_in: ed.cost_width_in ?? ln.cost_width_in,
    cost_height_in: ed.cost_height_in ?? ln.cost_height_in,
    total: Math.round(Number(qty || 0) * Number(unitPrice || 0) * 100) / 100,
    dirty: ed.qty != null || ed.unit_price != null || ed.item != null,
    price_overridden: ed.price_overridden ?? ln.price_overridden,
  };
};

const COLS =
  "lg:grid-cols-[minmax(0,1.9fr)_minmax(0,1.7fr)_76px_82px_104px_150px_150px]";
const cell = "text-[10px] uppercase tracking-widest text-zinc-500 lg:hidden";

const LineTotalInput = ({ line, onEdit }) => {
  const id = slug(line.key || line.id);
  const [draft, setDraft] = useState(null);
  const shown = draft ?? (line.total === 0 ? "" : String(line.total));

  const commit = (raw) => {
    setDraft(null);
    if (raw === "" || raw == null) return;
    const target = Number(raw);
    if (!Number.isFinite(target)) return;
    if (Math.abs(target - Number(line.total || 0)) < 0.005) return;
    if (
      !window.confirm(
        `Are you sure you want to change this line's amount to $${target.toFixed(
          2,
        )}? The unit price will be recalculated to match.`,
      )
    )
      return;
    const qty = Number(line.qty || 0);
    if (qty > 0) {
      onEdit(line, {
        unit_price: Math.round((target / qty) * 1e4) / 1e4,
        price_overridden: true,
      });
    } else {
      onEdit(line, {
        qty: 1,
        unit_price: Math.round(target * 100) / 100,
        price_overridden: true,
      });
    }
  };

  return (
    <Input
      data-testid={`line-total-${id}`}
      type="number"
      step="0.01"
      value={shown}
      title={
        line.price_overridden
          ? "Amount manually overridden — the unit price was back-solved to match"
          : "Type the amount you want this line to total — the unit price is worked out for you"
      }
      onChange={(e) => setDraft(e.target.value)}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      className={`h-10 border-zinc-800 bg-zinc-950 text-right font-mono-data text-sm font-bold tabular-nums ${
        line.price_overridden
          ? "border-red-500/60 text-red-400"
          : line.dirty
            ? "border-amber-400/50 text-amber-300"
            : "text-zinc-100"
      }`}
    />
  );
};

const LineRow = ({
  line,
  units,
  catalog,
  onEdit,
  onPick,
  onReset,
  onRemove,
  minMargin = 0,
  onDragStartLine,
  onDropOnLine,
}) => {
  const id = slug(line.key || line.id);
  const [calcOpen, setCalcOpen] = useState(false);
  const [over, setOver] = useState(false);
  const canCost = COST_KINDS.has(line.kind);
  const margin = lineMarginPct(line);
  const low = minMargin > 0 && margin != null && margin < minMargin;
  return (
    <div
      data-testid={`quote-line-${id}`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        onDragStartLine?.(line);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        onDropOnLine?.(line);
      }}
      className={`grid grid-cols-2 items-end gap-2 border-b px-2 py-2.5 transition-colors duration-150 hover:bg-zinc-900/60 lg:items-center ${COLS} ${
        over ? "border-indigo-400 bg-indigo-500/10" : "border-zinc-800/70"
      } ${low ? "bg-red-500/5" : ""} ${line.hidden ? "opacity-50" : ""}`}
    >
      <div className="col-span-2 lg:col-span-1">
        <span className={cell}>Item / sold as</span>
        <div className="flex items-center gap-2">
          <span
            data-testid={`line-drag-${id}`}
            title="Drag to reorder — drop on a line in another area to move it there"
            className="shrink-0 cursor-grab text-zinc-600 transition-colors duration-150 hover:text-zinc-300 active:cursor-grabbing"
          >
            <DotsSixVertical size={16} weight="bold" />
          </span>
          {line.manual ? (
            <button
              type="button"
              data-testid={`line-material-toggle-${id}`}
              onClick={() => onEdit(line, { is_material: !line.is_material })}
              title={
                line.is_material
                  ? "This manual line is the slab — color compares adjust its price. Click to unset."
                  : "Mark this manual line as the slab so color compares adjust its total"
              }
              className={`w-[74px] shrink-0 truncate rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                line.is_material
                  ? "bg-indigo-500/20 text-indigo-300"
                  : "bg-zinc-800 text-zinc-400 hover:text-indigo-300"
              }`}
            >
              {line.is_material ? "Slab" : "Manual"}
            </button>
          ) : (
            <span className="w-[74px] shrink-0 truncate rounded bg-zinc-800 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-zinc-400">
              {KIND_LABEL[line.kind] || line.kind}
            </span>
          )}
          <SearchSelect
            testId={`line-item-${id}`}
            value={line.item || ""}
            options={catalog || []}
            allowCustom
            tabbed
            placeholder="Pick or type an item"
            searchPlaceholder="Services, sinks, slabs, remnants…"
            emptyText="Type a name to use it as a custom line"
            onChange={(v) => onPick(line, v)}
            className="h-10 px-3 text-sm font-semibold"
          />
        </div>
      </div>
      <div className="col-span-2 lg:col-span-1">
        <span className={cell}>Description</span>
        <Input
          data-testid={`line-desc-${id}`}
          value={line.description || ""}
          onChange={(e) => onEdit(line, { description: e.target.value })}
          placeholder="—"
          className="h-10 border-zinc-800 bg-zinc-950 text-xs text-zinc-400"
        />
      </div>
      <div>
        <span className={cell}>Qty</span>
        <Input
          data-testid={`line-qty-${id}`}
          type="number"
          step="0.01"
          value={line.qty ?? ""}
          onChange={(e) => onEdit(line, { qty: e.target.value })}
          className="h-10 border-zinc-800 bg-zinc-950 text-right font-mono-data text-xs"
        />
      </div>
      <div>
        <span className={cell}>Unit</span>
        <Select
          value={line.unit || "EA"}
          onValueChange={(v) => onEdit(line, { unit: v })}
        >
          <SelectTrigger
            data-testid={`line-unit-${id}`}
            className="h-10 border-zinc-800 bg-zinc-950 text-xs"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(units || ["SF", "LF", "EA", "HR", "Job"]).map((u) => (
              <SelectItem key={u} value={u}>
                {u}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <span className={cell}>Unit price</span>
        <div className="flex items-center gap-1.5">
          <Input
            data-testid={`line-price-${id}`}
            type="number"
            step="0.01"
            value={line.unit_price ?? ""}
            onChange={(e) => onEdit(line, { unit_price: e.target.value })}
            className={`h-10 border-zinc-800 bg-zinc-950 text-right font-mono-data text-xs ${
              line.dirty ? "text-amber-300" : ""
            }`}
          />
          {canCost && (
            <button
              type="button"
              data-testid={`line-cost-calc-${id}`}
              onClick={() => setCalcOpen(true)}
              title="Cost & margin calculator"
              className={`flex h-10 w-9 shrink-0 items-center justify-center rounded-md border transition-colors duration-150 ${
                line.unit_cost
                  ? "border-emerald-400/50 bg-emerald-500/10 text-emerald-300"
                  : "border-zinc-800 bg-zinc-950 text-zinc-500 hover:text-indigo-300"
              }`}
            >
              <Calculator size={14} weight="bold" />
            </button>
          )}
        </div>
        {canCost && line.unit_cost ? (
          <div
            className={`mt-0.5 text-right text-[10px] uppercase tracking-widest ${
              low ? "text-red-300" : "text-emerald-300/80"
            }`}
            data-testid={`line-cost-note-${id}`}
          >
            cost {money(line.unit_cost)} · {margin != null ? `${margin}% margin` : `${line.cost_mode || "markup"} ${line.cost_pct || 0}%`}
          </div>
        ) : null}
      </div>
      <div className="text-right">
        <span className={cell}>Total</span>
        <LineTotalInput line={line} onEdit={onEdit} />
      </div>
      <div className="flex shrink-0 items-center justify-end gap-1.5">
        <button
          data-testid={`line-tax-${id}`}
          onClick={() => onEdit(line, { taxable: !line.taxable })}
          title={line.taxable ? "Taxable" : "Not taxed"}
          className={`rounded border px-1.5 py-1 text-[9px] font-bold uppercase tracking-widest transition-colors duration-150 ${
            line.taxable
              ? "border-indigo-400/40 bg-indigo-500/10 text-indigo-300"
              : "border-zinc-700 text-zinc-600 hover:text-zinc-400"
          }`}
        >
          Tax
        </button>
        <button
          data-testid={`line-hide-${id}`}
          onClick={() => onEdit(line, { hidden: !line.hidden })}
          title={
            line.hidden ? "Hidden on the printed quote" : "Shown on the quote"
          }
          className={`rounded border px-1.5 py-1 transition-colors duration-150 ${
            line.hidden
              ? "border-amber-400/40 bg-amber-500/10 text-amber-300"
              : "border-zinc-700 text-zinc-500 hover:text-zinc-300"
          }`}
        >
          {line.hidden ? (
            <EyeSlash size={13} weight="bold" />
          ) : (
            <Eye size={13} weight="bold" />
          )}
        </button>
        <button
          data-testid={`line-remove-${id}`}
          onClick={() => onRemove(line)}
          title="Delete this line"
          className="rounded border border-zinc-700 px-1.5 py-1 text-zinc-500 transition-colors duration-150 hover:border-red-500/40 hover:text-red-400"
        >
          <Trash size={13} weight="bold" />
        </button>
        {!line.manual && (
          <button
            data-testid={`line-reset-${id}`}
            onClick={() => onReset(line)}
            title="Reset to the calculated price"
            className="rounded border border-zinc-700 px-1.5 py-1 text-zinc-500 transition-colors duration-150 hover:border-indigo-400/40 hover:text-indigo-300"
          >
            <ArrowCounterClockwise size={13} weight="bold" />
          </button>
        )}
      </div>
      {canCost && (
        <CostMarginDialog
          line={line}
          open={calcOpen}
          onOpenChange={setCalcOpen}
          onApply={(patch) => onEdit(line, patch)}
        />
      )}
      {low && (
        <div
          className="col-span-2 text-[10px] font-bold uppercase tracking-widest text-red-300 lg:col-span-7"
          data-testid={`line-low-margin-${id}`}
        >
          Low margin · {margin}% (min {minMargin}%)
        </div>
      )}
    </div>
  );
};
const GroupHeader = ({
  title,
  sub,
  subtotal,
  onAdd,
  onAddService,
  onRename,
  onRemove,
  services,
  testId,
}) => (
  <div className="flex flex-wrap items-center gap-3 border-b border-zinc-800 bg-zinc-900/80 px-3 py-2.5">
    <div className="flex-1">
      {onRename ? (
        <div className="group flex items-center gap-1.5">
          <input
            data-testid={`${testId}-name`}
            value={title}
            onChange={(e) => onRename(e.target.value)}
            placeholder="Area name"
            onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
            title="Rename this area"
            aria-label="Area name"
            className="w-full max-w-[260px] rounded-sm border border-transparent bg-transparent px-1 -mx-1 text-sm font-bold tracking-tight text-zinc-100 outline-none transition-colors duration-150 hover:border-zinc-700 focus:border-amber-400/60 focus:bg-zinc-950"
          />
          <PencilSimple
            size={12}
            className="shrink-0 text-zinc-600 opacity-0 transition-opacity duration-150 group-hover:opacity-100"
          />
        </div>
      ) : (
        <div
          className="text-sm font-bold tracking-tight text-zinc-100"
          data-testid={`${testId}-name`}
        >
          {title || "Area"}
        </div>
      )}
      {sub && (
        <div className="font-mono-data text-[11px] text-zinc-500">{sub}</div>
      )}
    </div>
    <div
      className="font-mono-data text-sm font-bold text-zinc-200"
      data-testid={`${testId}-subtotal`}
    >
      {money(subtotal)}
    </div>
    <div className="w-44">
      <SearchSelect
        testId={`${testId}-service`}
        value=""
        options={services || []}
        placeholder="+ Add item"
        searchPlaceholder="Services, sinks, slabs, remnants…"
        emptyText="Nothing in the catalogue yet"
        onChange={(v) => onAddService(v)}
        className="h-9 text-xs"
      />
    </div>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid={`${testId}-add-line`}
          variant="outline"
          className="h-9 border-zinc-700 bg-transparent text-[10px] uppercase tracking-widest hover:bg-zinc-800"
        >
          <Plus size={13} weight="bold" className="mr-1" /> Line
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        data-testid={`${testId}-add-line-menu`}
        className="w-44 border-zinc-800 bg-zinc-900"
      >
        {LINE_TYPES.map((t) => (
          <DropdownMenuItem
            key={t.kind}
            data-testid={`${testId}-add-line-${t.kind}`}
            onSelect={() => onAdd(t.kind)}
            className="cursor-pointer text-sm text-zinc-200 focus:bg-indigo-500/15 focus:text-white"
          >
            {t.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
    {onRemove && (
      <button
        type="button"
        data-testid={`${testId}-remove`}
        onClick={onRemove}
        title="Remove this empty area"
        className="flex h-9 w-9 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 transition-colors duration-150 hover:border-red-500/40 hover:text-red-400"
      >
        <Trash size={14} weight="bold" />
      </button>
    )}
  </div>
);

export const QuoteLines = ({
  quoteId,
  totals,
  edits,
  areas,
  services = [],
  minMargin = 0,
  showProfit = true,
  discount,
  onDiscount,
  discountPct,
  onDiscountPct,
  notes,
  onNotes,
  onEdit,
  onReset,
  onRemove,
  onAdd,
  onAddService,
  onPickItem,
  onRenameArea,
  onAddArea,
  onRemoveArea,
  onSave,
  onReview,
  busy,
  removedCount = 0,
  onRestoreRemoved,
  materials = [],
  onAddOption,
  onEditOption,
  onRemoveOption,
  onUseOption,
  onEmailOptions,
}) => {
  const all = (totals?.lines || []).map((ln) => resolveLine(ln, edits));
  const [pctMode, setPctMode] = useState(Number(discountPct) > 0);
  const [dragged, setDragged] = useState(null);
  const lowLines = minMargin > 0
    ? all.filter((l) => {
        const m = lineMarginPct(l);
        return m != null && m < minMargin;
      })
    : [];

  /** Drop `dragged` where `target` sits: renumber that area and move across areas. */
  const dropOnLine = (target) => {
    if (!dragged || keyOf(dragged) === keyOf(target)) return setDragged(null);
    const src = dragged;
    setDragged(null);
    const destArea = target.area_id || "";
    const rest = all.filter((l) => keyOf(l) !== keyOf(src));
    const ordered = [];
    rest.forEach((l) => {
      if (keyOf(l) === keyOf(target)) ordered.push({ ...src, area_id: destArea });
      ordered.push(l);
    });
    ordered.forEach((l, i) => {
      const patch = { sort_order: i };
      if (keyOf(l) === keyOf(src) && (src.area_id || "") !== destArea)
        patch.move_area_id = destArea;
      onEdit(l, patch);
    });
  };
  const units = totals?.units || ["SF", "LF", "EA", "HR", "Job"];
  const groups = [
    ...(areas || []).map((a) => ({
      id: a.id,
      title: a.name ?? "",
      sub: [
        a.address,
        `${(totals?.areas || []).find((x) => x.id === a.id)?.sqft ?? 0} ft²`,
      ]
        .filter(Boolean)
        .join(" · "),
    })),
    { id: "", title: "Project charges", sub: "Charged across the whole job" },
  ];
  const localSubtotal =
    Math.round(all.reduce((s, l) => s + l.total, 0) * 100) / 100;
  const drift =
    totals && Math.abs(localSubtotal - Number(totals.subtotal || 0)) > 0.01;

  return (
    <div className="space-y-4" data-testid="quote-lines">
      {lowLines.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200"
          data-testid="quote-low-margin-banner"
        >
          <span className="font-bold uppercase tracking-widest">Low margin</span>
          <span>
            {lowLines.length} line{lowLines.length > 1 ? "s" : ""} under your {minMargin}% minimum:{" "}
            {lowLines.map((l) => l.item).filter(Boolean).join(", ")}
          </span>
        </div>
      )}
      {removedCount > 0 && (
        <div
          className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
          data-testid="quote-lines-removed-bar"
        >
          <span>
            {removedCount} calculated line{removedCount > 1 ? "s" : ""} deleted
            from this quote.
          </span>
          <button
            type="button"
            data-testid="quote-lines-restore"
            onClick={onRestoreRemoved}
            className="rounded-full border border-amber-400/40 px-3 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 hover:bg-amber-500/20"
          >
            Bring them back
          </button>
        </div>
      )}
      <div className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900/40">
        <div
          className={`hidden gap-2 border-b-2 border-zinc-800 bg-zinc-900 px-2 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500 lg:grid ${COLS}`}
        >
          <span>Item / sold as</span>
          <span>Description</span>
          <span className="text-right">Qty</span>
          <span>Unit</span>
          <span className="text-right">Unit price</span>
          <span className="text-right">Total</span>
          <span className="text-right">Tax · Hide</span>
        </div>
        {groups.map((g) => {
          const rows = all.filter((l) => l.area_id === g.id);
          if (!rows.length && g.id === "") return null;
          const sub =
            Math.round(rows.reduce((s, l) => s + l.total, 0) * 100) / 100;
          return (
            <div
              key={g.id || "project"}
              data-testid={`line-group-${g.id || "project"}`}
            >
              <GroupHeader
                title={g.title}
                sub={g.sub}
                subtotal={sub}
                services={services}
                onAdd={(kind) => onAdd(g.id, kind)}
                onAddService={(serviceId) => onAddService(g.id, serviceId)}
                onRename={
                  g.id && onRenameArea ? (name) => onRenameArea(g.id, name) : null
                }
                onRemove={
                  g.id &&
                  onRemoveArea &&
                  (areas || []).length > 1 &&
                  rows.length === 0 &&
                  !((areas || []).find((a) => a.id === g.id)?.pieces || []).length
                    ? () => onRemoveArea(g.id)
                    : null
                }
                testId={`line-group-${g.id || "project"}`}
              />
              {rows.length === 0 ? (
                <div className="px-3 py-4 text-xs text-zinc-500">
                  Nothing priced here yet — draw pieces on step 2 or add a line.
                </div>
              ) : (
                rows.map((l) => (
                  <LineRow
                    key={l.key || l.id}
                    line={l}
                    units={units}
                    catalog={services}
                    minMargin={minMargin}
                    onDragStartLine={setDragged}
                    onDropOnLine={dropOnLine}
                    onEdit={onEdit}
                    onPick={onPickItem}
                    onReset={onReset}
                    onRemove={onRemove}
                  />
                ))
              )}
              {(() => {
                const areaObj = (areas || []).find((a) => a.id === g.id);
                const opts = areaObj?.material_options || [];
                const priced =
                  (totals?.areas || []).find((x) => x.id === g.id)?.material_options || [];
                const showOpts = g.id && onAddOption && (rows.length > 0 || opts.length > 0);
                if (!showOpts) return null;
                return (
                  <div data-testid={`line-options-${g.id}`}>
                    {opts.map((o) => (
                      <MaterialOptionRow
                        key={o.id}
                        quoteId={quoteId}
                        areaId={g.id}
                        option={o}
                        priced={priced.find((p) => p.id === o.id)}
                        materials={materials}
                        onEdit={(optId, patch) => onEditOption(g.id, optId, patch)}
                        onRemove={(optId) => onRemoveOption(g.id, optId)}
                        onUse={(optId) => onUseOption(g.id, optId)}
                      />
                    ))}
                    <div className="flex flex-wrap items-center gap-3 border-b border-zinc-800/70 bg-zinc-950/40 py-2 pl-10">
                      <button
                        type="button"
                        data-testid={`line-add-option-${g.id}`}
                        onClick={() => onAddOption(g.id)}
                        title="Price another color side by side — the customer sees the difference"
                        className="flex items-center gap-1.5 rounded-full border border-amber-400/60 bg-amber-500/15 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-widest text-amber-200 shadow-sm transition-all duration-150 hover:-translate-y-px hover:border-amber-400 hover:bg-amber-500/25"
                      >
                        <Plus size={13} weight="bold" />
                        {opts.length ? "Compare another color" : "Compare a color"}
                      </button>
                      {onEmailOptions && priced.some((p) => p.priced) && (
                        <button
                          type="button"
                          data-testid={`line-email-options-${g.id}`}
                          onClick={onEmailOptions}
                          title="Email the customer a side-by-side so they can pick from their phone"
                          className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-300/80 transition-colors duration-150 hover:text-emerald-200"
                        >
                          <PaperPlaneTilt size={11} weight="bold" />
                          Email options to customer
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })}
        {onAddArea && (
          <div className="border-t border-zinc-800 bg-zinc-900/40 px-3 py-2.5">
            <button
              type="button"
              data-testid="quote-lines-add-area"
              onClick={onAddArea}
              title="Add another area (e.g. Island, Bathroom) — no drawing needed; price it with lines"
              className="flex h-9 items-center gap-1.5 rounded-full border border-dashed border-zinc-700 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:border-amber-400/60 hover:text-amber-200"
            >
              <Plus size={12} weight="bold" /> Add area
            </button>
          </div>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
        <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            Additional notes
          </div>
          <Textarea
            data-testid="quote-lines-notes"
            value={notes || ""}
            onChange={(e) => onNotes(e.target.value)}
            placeholder="Anything else the customer should see on this quote — lead times, exclusions, site conditions…"
            className="mt-2 min-h-[104px] border-zinc-800 bg-zinc-950 text-sm"
          />
          <p className="mt-3 text-xs leading-relaxed text-zinc-500">
            Prices come straight from the drawing and your Pricing settings.
            Change a quantity, a unit price or type straight into{" "}
            <span className="text-amber-300">Total</span> to land a line on an
            exact amount — that line then wins; the totals, the printed quote
            and the emailed quote all follow it.{" "}
            <span className="text-amber-300">Tax</span> controls whether a line
            is taxed; <span className="text-amber-300">Hide</span> keeps a line
            off the customer&apos;s copy but still charges it. Use{" "}
            <ArrowCounterClockwise size={12} weight="bold" className="inline" />{" "}
            to go back to the calculated price.
          </p>
        </div>
        <div
          className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
          data-testid="quote-lines-totals"
        >
          {[
            ["Subtotal", totals?.subtotal],
            ...(totals?.trade_discount
              ? [
                  [
                    `Trade discount (${totals.trade_pct}%)`,
                    -totals.trade_discount,
                  ],
                ]
              : []),
            ["Tax", totals?.tax],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between py-1 text-sm">
              <span className="text-zinc-500">{k}</span>
              <span className="font-mono-data text-zinc-200">{money(v)}</span>
            </div>
          ))}
          <div className="mt-2 flex items-center justify-between gap-3">
            <span className="text-sm text-zinc-500">Discount</span>
            <div className="flex items-center gap-2">
              <div
                className="flex overflow-hidden rounded-md border border-zinc-800"
                data-testid="quote-discount-mode"
              >
                {["$", "%"].map((m) => (
                  <button
                    key={m}
                    data-testid={`quote-discount-mode-${m === "$" ? "fixed" : "pct"}`}
                    onClick={() => {
                      if (m === "$") {
                        onDiscountPct(0);
                      } else {
                        onDiscount(0);
                        onDiscountPct(Number(discountPct) || 0);
                      }
                      setPctMode(m === "%");
                    }}
                    className={`h-10 w-9 text-xs font-bold transition-colors duration-150 ${
                      (m === "%") === pctMode
                        ? "bg-indigo-500 text-white"
                        : "bg-zinc-950 text-zinc-500 hover:text-zinc-300"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
              <Input
                data-testid="quote-lines-discount"
                type="number"
                step="0.01"
                value={pctMode ? discountPct : discount}
                onChange={(e) =>
                  pctMode
                    ? onDiscountPct(Number(e.target.value))
                    : onDiscount(Number(e.target.value))
                }
                className="h-10 w-24 border-zinc-800 bg-zinc-950 text-right font-mono-data text-sm"
              />
            </div>
          </div>
          {pctMode && Number(discountPct) > 0 && (
            <div
              className="mt-1 text-right text-[11px] text-amber-300"
              data-testid="quote-discount-applied"
            >
              −{money(totals?.discount)} off
            </div>
          )}
          <div className="mt-3 flex items-baseline justify-between border-t border-zinc-800 pt-3">
            <span className="text-xs font-bold uppercase tracking-widest text-zinc-400">
              Total
            </span>
            <span
              className="font-mono-data text-2xl font-bold text-indigo-300"
              data-testid="quote-lines-total"
            >
              {money(totals?.total)}
            </span>
          </div>
          {showProfit && totals?.cost_lines > 0 && (
            <div
              className="mt-3 rounded-md border border-emerald-500/25 bg-emerald-500/5 px-3 py-2"
              data-testid="quote-profit-strip"
            >
              <div className="flex items-center justify-between text-[11px]">
                <span className="font-bold uppercase tracking-widest text-emerald-300/80">
                  Shop profit
                </span>
                <span className="font-mono-data font-bold text-emerald-300">
                  {money(totals.profit)} · {totals.margin_pct}%
                </span>
              </div>
              <div className="mt-0.5 flex items-center justify-between text-[11px] text-zinc-500">
                <span className="font-mono-data">Cost {money(totals.cost_total)}</span>
                <span>
                  {totals.cost_lines} of {all.length} lines costed
                </span>
              </div>
            </div>
          )}
          <Button
            data-testid="quote-lines-save"
            disabled={busy}
            onClick={onSave}
            className="mt-4 h-11 w-full bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
          >
            {busy ? "Saving…" : "Save changes"}
          </Button>
          {onReview && (
            <Button
              data-testid="quote-lines-review"
              variant="outline"
              disabled={busy}
              onClick={onReview}
              className="mt-2 h-11 w-full border-zinc-700 bg-zinc-950 text-xs font-bold uppercase tracking-widest text-zinc-200 hover:bg-zinc-800"
            >
              Review quote
              <ArrowRight size={15} weight="bold" className="ml-2" />
            </Button>
          )}
          {drift && (
            <div
              className="mt-2 text-center text-[10px] uppercase tracking-widest text-amber-300"
              data-testid="quote-lines-pending"
            >
              Recalculating…
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteLines;
